import React, { useMemo } from 'react';
import {
  Box,
  Container,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
  Chip,
  useMediaQuery,
} from '@mui/material';
import { OutlinedIcons } from '@marageti/z4-lib';
import { Outlet } from 'react-router-dom';
import { AgentWithUnreadCount, Conversation } from '@marageti/z4-sdk/lib/travel';
import ConversationList from './conversation-list';
import InboxAgentSelector from './inbox-agent-selector';

const {
  OutlinedChevronDoubleRightIcon,
  OutlinedChatIcon,
} = OutlinedIcons;

const SIDEBAR_OPEN_WIDTH = 320;

const CONVERSATION_LIST_SKELETON_IDS = ['sk-1', 'sk-2', 'sk-3', 'sk-4', 'sk-5', 'sk-6', 'sk-7', 'sk-8'];

const ConversationListSkeleton = () => (
  <Box
    sx={{
      width: '100%',
      minWidth: 0,
      flex: 1,
      overflowY: { desktop: 'auto' },
    }}
  >
    <List
      component={Stack}
      spacing={1.5}
      sx={{
        width: '100%',
        pr: 2,
      }}
    >
      {CONVERSATION_LIST_SKELETON_IDS.map((id) => (
        <ListItem
          key={id}
          sx={{
            width: '100%',
            p: 0,
            pr: 2,
            borderRadius: '12px',
          }}
        >
          <ListItemButton
            disabled
            sx={{
              borderRadius: '12px',
              p: 1.5,
              pr: 1,
              minHeight: 56,
              width: '100%',
            }}
            disableRipple
          >
            <ListItemIcon sx={{ minWidth: 56 }}>
              <Skeleton variant="circular" width={40} height={40} sx={{ display: 'block' }} />
            </ListItemIcon>
            <ListItemText
              disableTypography
              primary={(
                <Box display="flex" alignItems="center" minWidth={0}>
                  <Skeleton variant="text" width="70%" height={20} sx={{ display: 'block', flexShrink: 0 }} />
                </Box>
              )}
              secondary={(
                <Box display="flex" alignItems="center" minWidth={0} sx={{ mt: 0.5 }}>
                  <Skeleton variant="text" width={40} height={16} sx={{ display: 'block', flexShrink: 0 }} />
                </Box>
              )}
            />
          </ListItemButton>
        </ListItem>
      ))}
    </List>
  </Box>
);

export type MessageInboxViewProps = {
  conversationsMap: Record<string, Conversation>;
  hasConversations: boolean;
  hasMoreConversations: boolean;
  isInitialLoad: boolean;
  isLeftSidebarOpen: boolean;
  isLoading: boolean;
  isValidating?: boolean;
  loggedInAgentId: string | undefined;
  isAgentAdmin: boolean;
  markConversationRead: (id: string) => void;
  markConversationUnread: (id: string) => void;
  onAgentSelect: (agent: AgentWithUnreadCount | null) => void;
  onLoadMoreConversations: () => void;
  onSelectConversation: (conversationId: string) => void;
  onClickUnread: () => void;
  partnerAgentsWithUnread?: AgentWithUnreadCount[];
  selectedAgentPersonId: string | null;
  selectedInboxConversationId: string | undefined;
  showUnreadOnly: boolean;
  toggleLeftSidebar: () => void;
  unreadCount: number | undefined;
  selectedAgentId: string;
};

const MessageInboxView = ({
  conversationsMap,
  hasConversations,
  hasMoreConversations,
  isInitialLoad,
  isLeftSidebarOpen,
  isLoading,
  isValidating,
  loggedInAgentId,
  isAgentAdmin,
  markConversationRead,
  markConversationUnread,
  onAgentSelect,
  onLoadMoreConversations,
  onSelectConversation,
  onClickUnread,
  partnerAgentsWithUnread,
  selectedAgentPersonId,
  selectedInboxConversationId,
  showUnreadOnly,
  toggleLeftSidebar,
  unreadCount,
  selectedAgentId,
}: MessageInboxViewProps) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('desktop'));
  const showSidebarOnly = isMobile && !selectedInboxConversationId;
  const showChatFullscreen = isMobile && selectedInboxConversationId;
  const sidebarWidth = isMobile ? '100%' : `${SIDEBAR_OPEN_WIDTH}px`;

  const selectedAgent = useMemo(
    () => partnerAgentsWithUnread?.find((a) => a.id === selectedAgentPersonId) ?? null,
    [partnerAgentsWithUnread, selectedAgentPersonId],
  );
  const inboxTitle = selectedAgent ? `${selectedAgent.fullName}'s Inbox` : 'Your Inbox';

  return (
    <Container maxWidth="xl" sx={{ height: 'calc(100vh - 93px)', display: 'flex', flexDirection: 'column' }}>
      <Box
        display="flex"
        flexDirection="row"
        justifyContent="space-between"
        sx={{
          height: '100%',
        }}
      >
        {/* Left Sidebar */}
        <Collapse
          in={isLeftSidebarOpen && !showChatFullscreen}
          orientation="horizontal"
          collapsedSize={0}
          sx={{
            mt: 3,
            mb: 1,
            maxWidth: sidebarWidth,
            flex: isMobile ? 1 : '0 0 auto',
            minWidth: 0,
            '& .MuiCollapse-wrapperInner': {
              width: sidebarWidth,
            },
          }}
        >
          <Box
            sx={{
              width: sidebarWidth,
              minWidth: 0,
              height: { desktop: '100%' },
              display: { desktop: 'flex' },
              flexDirection: 'column',
            }}
          >
            {/* Filter Header */}
            <Stack
              spacing={{ desktop: 1.5 }}
              mb={{ mobile: 1, desktop: 3 }}
              sx={{ pr: { desktop: 8, mobile: 0 } }}
            >
              <InboxAgentSelector
                loggedInAgentId={loggedInAgentId}
                isAgentAdmin={isAgentAdmin}
                partnerAgentsWithUnread={partnerAgentsWithUnread}
                selectedAgentPersonId={selectedAgentPersonId}
                onAgentSelect={onAgentSelect}
              />
              <Chip
                color={showUnreadOnly ? 'primary' : 'default'}
                onClick={onClickUnread}
                label={`Unread (${unreadCount ?? '-'})`}
                variant={showUnreadOnly ? 'filled' : 'outlined'}
                sx={{ width: 'fit-content' }}
              />
            </Stack>

            {/* Sidebar Content - only show skeleton on initial load; when loading more we keep showing the list so scroll isn't lost */}
            {isLoading && !hasConversations && isInitialLoad && (
              <ConversationListSkeleton />
            )}
            {hasConversations && (
              <ConversationList
                conversationsMap={conversationsMap}
                hasMoreConversations={hasMoreConversations}
                isValidating={isValidating}
                markConversationRead={markConversationRead}
                markConversationUnread={markConversationUnread}
                onLoadMoreConversations={onLoadMoreConversations}
                onSelectConversation={onSelectConversation}
                selectedInboxConversationId={selectedInboxConversationId}
                selectedAgentId={selectedAgentId}
              />
            )}
            {/* Empty conversation list - single line for both mobile & desktop */}
            {!hasConversations && !isLoading && (
              <Typography variant="body" color="text.secondary" sx={{ mt: 2, px: 1 }}>
                No conversations found.
              </Typography>
            )}
          </Box>
        </Collapse>

        {/* Main Content Area */}
        {!showSidebarOnly && (
          <Box
            sx={showChatFullscreen ? {
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1300,
              px: 0,
              pb: 6,
              backgroundColor: 'background.default',
            } : {
              display: 'flex',
              flex: 1,
              height: '100%',
              borderLeft: (theme) => `1px solid ${theme.palette.divider}`,
              mb: 1,
              position: 'relative',
              minWidth: 0,
            }}
          >
            {/* Sidebar Toggle Button */}
            {!isMobile && (
              <IconButton
                aria-label={isLeftSidebarOpen ? 'Close Messages List' : 'Open Messages List'}
                onClick={toggleLeftSidebar}
                sx={{
                  position: 'absolute',
                  top: '24px',
                  left: isLeftSidebarOpen ? '-57px' : '-41px',
                  transform: isLeftSidebarOpen ? 'rotate(180deg)' : 'none',
                  transition: 'left 300ms ease-in-out',
                }}
              >
                <OutlinedChevronDoubleRightIcon color="primary" />
              </IconButton>
            )}

            {/* Content Container */}
            <Box display="flex" flexDirection="row" flex={1} sx={{ minWidth: 0 }}>
              {selectedInboxConversationId ? (
                <Box flex={1} sx={{ minWidth: 0 }}>
                  <Outlet />
                </Box>
              ) : (
                <Box width="100%">
                  {/* Loading State - only on initial load, not when refetching (e.g. agent change) */}
                  {isLoading && !hasConversations && (
                    <Stack spacing={2} mt="162px" alignItems="center" width="100%">
                      <Skeleton variant="circular" width={48} height={48} />
                      <Skeleton variant="text" width={200} height={32} />
                      <Skeleton variant="text" width={280} height={24} />
                    </Stack>
                  )}

                  {/* Default Inbox View */}
                  {(hasConversations || (!isLoading && !hasConversations)) && (
                    <Stack spacing={2} mt="162px" alignItems="center" width="100%">
                      <OutlinedChatIcon color="primary" sx={{ fontSize: '32px' }} />
                      <Stack spacing={1}>
                        <Typography variant="h3" textAlign="center">
                          {inboxTitle}
                        </Typography>
                        <Typography textAlign="center" color="text.secondary">Select a conversation to see it here.</Typography>
                      </Stack>
                    </Stack>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        )}
      </Box>
    </Container>
  );
};

export default MessageInboxView;
